import type { Assistant } from "@inrev/assistant";
import type { TagColor } from "@portal/components/primitives";
import { DateTime } from "luxon";

type OrgChartEntity = Assistant.Dtos.Agent.OrgChart.Entity;
type Answer = Assistant.Dtos.Agent.OrgChart.Answer;
type Relationship = Assistant.Dtos.Agent.OrgChart.Relationship;

export const ANSWER_LABELS: Record<Answer, string> = {
	yes: "Yes",
	no: "No",
	not_described: "Not described",
};

export const RELATIONSHIP_LABELS: Record<Relationship, string> = {
	loanToEntity: "Loan to entity",
	loanFromEntity: "Loan from entity",
	arFromEntity: "Receivable from entity",
	apToEntity: "Payable to entity",
	rentPaidToEntity: "Rent paid to entity",
	entityOwnsRealEstate: "Entity owns real estate used",
	equipmentRentedFromEntity: "Equipment rented from entity",
	entityOwnsEquipment: "Entity owns equipment used",
	companyInvestsInEntity: "Equity investment in entity",
	entityIsVendor: "Entity is a vendor",
	companyWorksForEntity: "Entity is a customer",
};

const SUBSEQUENT_EVENT_LABELS: Record<NonNullable<OrgChartEntity["subsequentEvent"]>, string> = {
	acquired: "Acquired after FYE",
	formed: "Formed after FYE",
	sold: "Sold after FYE",
	wound_down: "Wound down after FYE",
};

export const PARENT_BASIS_LABELS: Record<NonNullable<OrgChartEntity["parentBasis"]>, string> = {
	stated: "Stated in the documents",
	derived: "Derived from a stated stake or control",
};

type EntityTag = { label: string; color: TagColor };

/** Only what the notes affirm gets a tag; silence stays untagged, so it never reads as No. */
export const entityTags = (entity: OrgChartEntity): EntityTag[] => [
	...(entity.vie === "yes" ? [{ label: "VIE", color: "blue" as const }] : []),
	...(entity.commonOwnerFullyOwned === "yes"
		? [{ label: "Common owner", color: "gray" as const }]
		: []),
	...(entity.subsequentEvent === null
		? []
		: [{ label: SUBSEQUENT_EVENT_LABELS[entity.subsequentEvent], color: "yellow" as const }]),
	...(entity.parentBasis === "derived"
		? [{ label: "Parent derived", color: "gray" as const }]
		: []),
	...(entity.changeSincePrior === "new"
		? [{ label: "New since prior FYE", color: "green" as const }]
		: entity.changeSincePrior === "changed"
			? [{ label: "Changed since prior FYE", color: "yellow" as const }]
			: []),
];

/** "Dec 31, 2025" from the notes' ISO date, or the text as the tool gave it when it is not one. */
const periodLabel = (value: string): string => {
	const parsed = DateTime.fromISO(value);
	return parsed.isValid ? parsed.toLocaleString(DateTime.DATE_MED) : value;
};

/** One sentence of context under the title, so the diagram stays the first thing read. */
export const periodSentence = (periodEnd: string | null, priorPeriodEnd: string | null) => {
	if (periodEnd === null) return "Fiscal year end not stated in the documents.";
	const current = `Fiscal year ended ${periodLabel(periodEnd)}`;
	return priorPeriodEnd === null
		? `${current}. No prior year compared.`
		: `${current}, compared with ${periodLabel(priorPeriodEnd)}.`;
};

/** The stakes the documents state, or null when they state none, so the row is left out. */
export const ownershipLabel = (
	entity: OrgChartEntity,
	entityById: ReadonlyMap<number, OrgChartEntity>,
) => {
	const stakes = entity.stakes.map(
		(stake) =>
			`${stake.pct ?? "A stake"} of ${entityById.get(stake.entityId)?.name ?? "another entity"}`,
	);
	const parts = [
		entity.ownershipPct === null ? null : `${entity.ownershipPct} by its parent`,
		...stakes,
	];
	const stated = parts.filter((part) => part !== null);
	return stated.length === 0 ? null : stated.join("; ");
};

export const CONSOLIDATION_LABELS: Record<Answer, string> = {
	yes: "Consolidated",
	no: "Not consolidated",
	not_described: "Consolidation not described",
};
